import React, { useCallback, useEffect, useState } from 'react';
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CFormCheck,
    CFormInput,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CNav,
    CNavItem,
    CNavLink,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from '@coreui/react-pro';
import { useSearchParams } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useFleet } from '../../contexts/FleetContext';
import { AssignmentCandidate, FleetDelivery, FleetDeliveryDetail } from '../../types/fleet';
import { showToastTR } from '../../Utils/BaseApp';
import { StatusBadge, formatCurrency, formatDateTime, getApiErrorMessage } from '../orders/orderUtils';
import { FleetGuard, FleetHeader } from './FleetHeader';
import { CourierStateBadge, DelayIndicator, PAYMENT_LABELS, canAssign } from './fleetUtils';

type Tab = 'active' | 'unassigned' | 'history';
const TABS: { key: Tab; label: string }[] = [
    { key: 'active', label: 'En curso' },
    { key: 'unassigned', label: 'Sin domiciliario' },
    { key: 'history', label: 'Historial' },
];
const REFRESH_MS = 20000;

/* ------------------------------------------------------------------ Asignar / reasignar */
const AssignModal: React.FC<{ companyId: number; delivery: FleetDelivery | null; onClose: () => void; onAssigned: () => void }> = ({ companyId, delivery, onClose, onAssigned }) => {
    const [candidates, setCandidates] = useState<AssignmentCandidate[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setCandidates([]);
        setSelectedId(null);
        setError(null);
        if (!delivery) return;
        setLoading(true);
        DomicilappService.getAssignmentCandidates(companyId, delivery.id)
            .then((response) => setCandidates(response?.data ?? []))
            .catch((err) => setError(getApiErrorMessage(err, 'No fue posible cargar los domiciliarios')))
            .finally(() => setLoading(false));
    }, [companyId, delivery]);

    const isReassign = !!delivery?.courier;
    const selected = candidates.find((c) => c.id === selectedId);

    const assign = () => {
        if (!delivery || !selected) return;
        setSaving(true);
        setError(null);
        DomicilappService.assignFleetDelivery(companyId, delivery.id, selected.id)
            .then((response) => {
                if (response?.status === 200) {
                    showToastTR(`Entrega #${delivery.id} asignada a ${selected.name}`, 3000, 'success');
                    onAssigned();
                    onClose();
                } else {
                    setError(response?.message ?? 'No fue posible asignar el pedido');
                }
            })
            .catch((err) => setError(getApiErrorMessage(err, 'No fue posible asignar el pedido')))
            .finally(() => setSaving(false));
    };

    return (
        <CModal visible={!!delivery} onClose={onClose} size="lg" alignment="center" scrollable>
            <CModalHeader>
                <CModalTitle>{isReassign ? 'Reasignar' : 'Asignar'} entrega #{delivery?.id}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                {delivery && (
                    <p className="small text-body-secondary">
                        Recoger en <strong className="text-body">{delivery.store.name}</strong>
                        {delivery.store.address ? ` (${delivery.store.address})` : ''} · destino {delivery.destination.municipality ?? '—'}
                        {delivery.destination.distance_km !== null ? `, ${delivery.destination.distance_km} km` : ''} · domicilio {formatCurrency(delivery.delivery_value)}.
                        {isReassign && <> Actualmente con <strong className="text-body">{delivery.courier?.name}</strong>: se le devuelve la comisión y queda libre.</>}
                    </p>
                )}
                <CAlert color="info" className="small py-2">
                    La asignación es directa: el domiciliario recibe una notificación y el pedido queda a su nombre, sin esperar a que acepte.
                </CAlert>
                {error && <CAlert color="danger" className="small py-2">{error}</CAlert>}

                {loading ? (
                    <div className="text-center py-4"><CSpinner size="sm" /></div>
                ) : candidates.length === 0 ? (
                    <p className="text-body-secondary small mb-0">No hay domiciliarios en tu flota.</p>
                ) : (
                    candidates.map((candidate) => (
                        <label
                            key={candidate.id}
                            className={`d-flex align-items-center gap-3 border rounded-3 p-2 mb-2 ${candidate.eligible ? '' : 'opacity-50'}`}
                            style={{ cursor: candidate.eligible ? 'pointer' : 'not-allowed' }}
                        >
                            <CFormCheck
                                type="radio"
                                name="assign-courier"
                                disabled={!candidate.eligible}
                                checked={selectedId === candidate.id}
                                onChange={() => setSelectedId(candidate.id)}
                            />
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-semibold text-body">{candidate.name}</span>
                                    <CourierStateBadge state={candidate.state} />
                                </div>
                                <small className="text-body-secondary">
                                    {candidate.distance_to_store_km !== null ? `${candidate.distance_to_store_km} km al comercio` : 'Sin ubicación'}
                                    {' · '}saldo {formatCurrency(candidate.balance)} · comisión {formatCurrency(candidate.commission)}
                                </small>
                                {!candidate.eligible && <small className="d-block text-danger">{candidate.reasons.join(' · ')}</small>}
                            </div>
                        </label>
                    ))
                )}
            </CModalBody>
            <CModalFooter>
                <CButton color="light" onClick={onClose}>Cancelar</CButton>
                <CButton color="dark" disabled={!selected || saving} onClick={assign}>
                    {saving && <CSpinner size="sm" className="me-1" />}
                    {isReassign ? 'Reasignar' : 'Asignar'}{selected ? ` a ${selected.name.split(' ')[0]}` : ''}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

/* ------------------------------------------------------------------ Detalle */
const DetailModal: React.FC<{ companyId: number; deliveryId: number | null; onClose: () => void }> = ({ companyId, deliveryId, onClose }) => {
    const [detail, setDetail] = useState<FleetDeliveryDetail | null>(null);

    useEffect(() => {
        setDetail(null);
        if (!deliveryId) return;
        DomicilappService.getFleetDelivery(companyId, deliveryId)
            .then((response) => setDetail(response?.data ?? null))
            .catch(() => setDetail(null));
    }, [companyId, deliveryId]);

    return (
        <CModal visible={deliveryId !== null} onClose={onClose} alignment="center" scrollable>
            <CModalHeader>
                <CModalTitle>Entrega #{deliveryId}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                {!detail ? (
                    <div className="text-center py-4"><CSpinner size="sm" /></div>
                ) : (
                    <>
                        <div className="small mb-3">
                            <div><span className="text-body-secondary">Comercio:</span> {detail.store.name} {detail.store.address ? `· ${detail.store.address}` : ''}</div>
                            <div><span className="text-body-secondary">Destino:</span> {detail.destination.municipality ?? '—'}{detail.destination.distance_km !== null ? ` · ${detail.destination.distance_km} km` : ''}</div>
                            <div><span className="text-body-secondary">Domicilio:</span> {formatCurrency(detail.delivery_value)} · {PAYMENT_LABELS[detail.payment]}</div>
                            <div><span className="text-body-secondary">Domiciliario:</span> {detail.courier?.name ?? 'Sin asignar'}</div>
                        </div>
                        <h6 className="mb-2">Estados</h6>
                        <ul className="list-unstyled small mb-3">
                            {detail.timeline.length === 0 && <li className="text-body-secondary">Sin cambios de estado todavía.</li>}
                            {detail.timeline.map((step, i) => (
                                <li key={i} className="d-flex justify-content-between border-bottom py-1">
                                    <span style={{ textTransform: 'capitalize' }}>{step.status_name}</span>
                                    <span className="text-body-secondary">{formatDateTime(step.created_at)}</span>
                                </li>
                            ))}
                        </ul>
                        <h6 className="mb-2">Ofertas a domiciliarios</h6>
                        <ul className="list-unstyled small mb-0">
                            {detail.offers.length === 0 && <li className="text-body-secondary">Aún no se ha ofrecido.</li>}
                            {detail.offers.map((offer, i) => (
                                <li key={i} className="d-flex justify-content-between border-bottom py-1">
                                    <span>{offer.courier_name} · <span className="text-body-secondary">{offer.offer_status}</span></span>
                                    <span className="text-body-secondary">{formatDateTime(offer.assigned_at)}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </CModalBody>
        </CModal>
    );
};

/* ------------------------------------------------------------------ Página */
const FleetDeliveriesContent: React.FC = () => {
    const { company } = useFleet();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = (TABS.find((t) => t.key === searchParams.get('tab'))?.key ?? 'active') as Tab;
    const [deliveries, setDeliveries] = useState<FleetDelivery[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [assigning, setAssigning] = useState<FleetDelivery | null>(null);
    const [detailId, setDetailId] = useState<number | null>(null);

    const load = useCallback(
        (silent = false) => {
            if (!company) return;
            if (!silent) setLoading(true);
            DomicilappService.getFleetDeliveries(company.id, { group: tab, search: search.trim() || undefined, per_page: 100 })
                .then((response) => setDeliveries(response?.data?.items ?? []))
                .catch(() => !silent && setDeliveries([]))
                .finally(() => !silent && setLoading(false));
        },
        [company, tab, search],
    );

    useEffect(() => {
        const timeout = setTimeout(() => load(), search ? 400 : 0);
        if (tab === 'history') return () => clearTimeout(timeout);
        const interval = setInterval(() => load(true), REFRESH_MS);
        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
        };
    }, [load, tab, search]);

    return (
        <>
            <FleetHeader title="Pedidos de la flota" description="Pedidos de tus domiciliarios y pedidos de tu municipio que esperan domiciliario" />

            <CCard>
                <CCardHeader className="pt-3 pb-0 d-flex flex-wrap justify-content-between align-items-end gap-2">
                    <CNav variant="tabs" className="card-header-tabs border-0">
                        {TABS.map((t) => (
                            <CNavItem key={t.key}>
                                <CNavLink active={t.key === tab} onClick={() => setSearchParams({ tab: t.key })} style={{ cursor: 'pointer' }}>
                                    {t.label}
                                </CNavLink>
                            </CNavItem>
                        ))}
                    </CNav>
                    <CFormInput size="sm" className="mb-2" placeholder="# entrega, comercio o domiciliario" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
                </CCardHeader>
                <CCardBody className="p-0">
                    <CTable responsive hover align="middle" className="mb-0">
                        <CTableHead>
                            <CTableRow>
                                <CTableHeaderCell>Entrega</CTableHeaderCell>
                                <CTableHeaderCell>Comercio</CTableHeaderCell>
                                <CTableHeaderCell>Destino</CTableHeaderCell>
                                <CTableHeaderCell>Estado</CTableHeaderCell>
                                <CTableHeaderCell>Tiempo</CTableHeaderCell>
                                <CTableHeaderCell>Domiciliario</CTableHeaderCell>
                                <CTableHeaderCell>Domicilio</CTableHeaderCell>
                                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                            </CTableRow>
                        </CTableHead>
                        <CTableBody>
                            {loading ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={8} className="text-center py-5"><CSpinner size="sm" /></CTableDataCell>
                                </CTableRow>
                            ) : deliveries.length === 0 ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={8} className="text-center py-5 text-body-secondary">
                                        {tab === 'unassigned' ? 'No hay pedidos esperando domiciliario.' : 'No hay pedidos para mostrar.'}
                                    </CTableDataCell>
                                </CTableRow>
                            ) : (
                                deliveries.map((delivery) => (
                                    <CTableRow key={delivery.id}>
                                        <CTableDataCell>
                                            <div className="fw-semibold text-body">#{delivery.id}</div>
                                            <small className="text-body-secondary">{formatDateTime(delivery.created_at)}</small>
                                            {delivery.is_scheduled && <CBadge color="warning-subtle" className="text-warning ms-1">Programado</CBadge>}
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <div className="text-body">{delivery.store.name}</div>
                                            <small className="text-body-secondary text-truncate d-block" style={{ maxWidth: 220 }}>{delivery.store.address}</small>
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <div className="text-body">{delivery.destination.municipality ?? '—'}</div>
                                            <small className="text-body-secondary">{delivery.destination.distance_km !== null ? `${delivery.destination.distance_km} km` : ''}</small>
                                        </CTableDataCell>
                                        <CTableDataCell><StatusBadge name={delivery.status_name} color={delivery.status_color} /></CTableDataCell>
                                        <CTableDataCell>
                                            {tab === 'history' ? <span className="text-body-secondary">—</span> : <DelayIndicator delivery={delivery} />}
                                            {delivery.status_id === 1 && <small className="d-block text-body-secondary">{delivery.offers_count} ofertas</small>}
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            {delivery.courier ? (
                                                <span className={delivery.courier.is_own ? 'text-body' : 'text-body-secondary'}>{delivery.courier.name}</span>
                                            ) : (
                                                <span className="text-warning">Buscando</span>
                                            )}
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <div className="text-body">{formatCurrency(delivery.delivery_value)}</div>
                                            <small className="text-body-secondary">{PAYMENT_LABELS[delivery.payment]}</small>
                                        </CTableDataCell>
                                        <CTableDataCell className="text-end text-nowrap">
                                            {canAssign(delivery) && tab !== 'history' && (
                                                <CButton size="sm" color="dark" className="me-2" onClick={() => setAssigning(delivery)}>
                                                    {delivery.courier ? 'Reasignar' : 'Asignar'}
                                                </CButton>
                                            )}
                                            <CButton size="sm" color="light" onClick={() => setDetailId(delivery.id)}>Ver</CButton>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))
                            )}
                        </CTableBody>
                    </CTable>
                </CCardBody>
            </CCard>

            {company && <AssignModal companyId={company.id} delivery={assigning} onClose={() => setAssigning(null)} onAssigned={() => load(true)} />}
            {company && <DetailModal companyId={company.id} deliveryId={detailId} onClose={() => setDetailId(null)} />}
        </>
    );
};

const FleetDeliveriesPage: React.FC = () => (
    <FleetGuard>
        <FleetDeliveriesContent />
    </FleetGuard>
);

export default FleetDeliveriesPage;
